/**
 * §P.10 · Las licencias federativas.
 *
 * ## Se ordena por lo que caduca antes, y ese es todo el diseño
 *
 * **Sin licencia en vigor el alumno no compite**, y eso se descubre el sábado por la
 * mañana en la mesa de inscripciones, con la familia delante y el niño con el dobok
 * puesto. `days_to_expiry` viene resuelto del servidor justo para que esta pantalla
 * pueda ponerlo primero.
 *
 * Renovar una licencia no es inmediato —hay federaciones que tardan semanas—, así que
 * el ámbar empieza a los treinta días y no a los siete.
 *
 * ## Las licencias son de dueño y administrativo
 *
 * Las tres rutas declaran `own` y `adm`, y es la excepción de §P.10: en todo lo demás
 * el entrenador entra, porque es quien lleva a los niños. Esto son papeles y dinero.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { euros, fecha } from '@/shared/ui/formato';
import { FaltaAntes } from '@/shared/ui/FaltaAntes';
import type { Alumno, PaginaDeAlumnos } from '@/club/alumnos/tipos';
import {
  cuantoQuedaDeLicencia,
  ESTADOS_DE_LICENCIA,
  urgenciaDeLicencia,
  type EstadoDeLicencia,
  type LicenciaFederativa,
} from '@/club/competicion/tipos';

interface Campos {
  student_id: string;
  federation: string;
  license_number: string;
  season: string;
  valid_to: string;
  precio: string;
}

const VACIO: Campos = {
  student_id: '',
  federation: '',
  license_number: '',
  season: '',
  valid_to: '',
  precio: '',
};

const oNulo = (v: string): string | null => (v.trim() === '' ? null : v.trim());

export function Licencias({
  club,
  puedeEditar,
}: {
  club: string;
  puedeEditar: boolean;
}) {
  const cache = useQueryClient();
  const [creando, setCreando] = useState(false);
  const [campos, setCampos] = useState<Campos>(VACIO);
  const [problema, setProblema] = useState<Problema | null>(null);

  const licencias = useQuery({
    queryKey: ['licencias', club],
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        ['federation-licenses'].get({ query: {} });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as LicenciaFederativa[];
    },
  });

  const alumnos = useQuery({
    queryKey: ['alumnos-para-licencia', club],
    enabled: creando,
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club }).students.get({
        query: { limit: 200, status: 'active' },
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as PaginaDeAlumnos;
    },
  });

  const refrescar = () => {
    setProblema(null);
    void cache.invalidateQueries({ queryKey: ['licencias', club] });
  };

  const crear = useMutation({
    mutationFn: async (c: Campos) => {
      const r = await api.v1.clubs({ club })['federation-licenses'].post({
        student_id: c.student_id,
        federation: c.federation.trim(),
        license_number: oNulo(c.license_number),
        season: oNulo(c.season),
        valid_to: oNulo(c.valid_to),
        fee_cents:
          c.precio.trim() === ''
            ? null
            : Math.round(Number(c.precio.replace(',', '.')) * 100),
        status: 'active',
      });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setCreando(false);
      setCampos(VACIO);
      refrescar();
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  const cambiar = useMutation({
    mutationFn: async ({
      id,
      cambios,
    }: {
      id: string;
      cambios: Record<string, unknown>;
    }) => {
      const r = await api.v1
        .clubs({ club })
        ['federation-licenses']({ licenseId: id })
        .patch(cambios);
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: refrescar,
    onError: (e) => setProblema(aProblema(e)),
  });

  if (licencias.isPending) {
    return <Cargando filas={4} etiqueta="Cargando las licencias" />;
  }

  if (licencias.isError || !licencias.data) {
    return (
      <PanelError
        problema={aProblema(licencias.error)}
        reintentar={() => void licencias.refetch()}
      />
    );
  }

  /*
   * Por lo que caduca antes. El servidor las manda por fecha, pero el orden que
   * importa aquí es el del reloj: una caducada la semana pasada va delante de una
   * que caduca en junio, aunque se registrara después.
   */
  const ordenadas = [...licencias.data].sort(
    (a, b) => (a.days_to_expiry ?? 9999) - (b.days_to_expiry ?? 9999)
  );

  const enRiesgo = ordenadas.filter(
    (l) =>
      l.status === 'active' &&
      l.days_to_expiry !== null &&
      l.days_to_expiry <= 30
  ).length;

  return (
    <section>
      <header className="titulo-fila">
        <h1>Licencias federativas</h1>
        {puedeEditar && !creando && (
          <button
            type="button"
            className="btn-primario"
            onClick={() => setCreando(true)}
          >
            Registrar una
          </button>
        )}
      </header>

      <p className="tenue">
        <strong>Sin licencia en vigor el alumno no compite.</strong> Lo normal
        es descubrirlo el sábado por la mañana en la mesa de inscripciones; esta
        lista existe para que no pase.
      </p>

      {problema && <PanelError problema={problema} />}

      {enRiesgo > 0 && (
        <p className="nota">
          <strong>
            {enRiesgo === 1
              ? 'Una licencia caduca este mes'
              : `${enRiesgo} licencias caducan este mes`}
            .
          </strong>{' '}
          Renovarlas no es inmediato: hay federaciones que tardan semanas.
        </p>
      )}

      {creando && (
        <form
          className="formulario"
          onSubmit={(e) => {
            e.preventDefault();
            setProblema(null);
            crear.mutate(campos);
          }}
        >
          <div className="rejilla">
            <label>
              Alumno
              <select
                required
                value={campos.student_id}
                onChange={(e) =>
                  setCampos({ ...campos, student_id: e.target.value })
                }
              >
                <option value="">Elige</option>
                {(alumnos.data?.data ?? []).map((a: Alumno) => (
                  <option key={a.id} value={a.id}>
                    {a.first_name} {a.last_name ?? ''}
                  </option>
                ))}
              </select>
            </label>

            {alumnos.data?.data.length === 0 && (
              <FaltaAntes
                accion="Dar de alta un alumno"
                destino="/alumnos"
                nuevo
              >
                Tu club todavía no tiene alumnos activos, y la licencia es de
                uno.
              </FaltaAntes>
            )}

            <label>
              Federación
              <input
                required
                maxLength={40}
                placeholder="FMTKD"
                value={campos.federation}
                onChange={(e) =>
                  setCampos({ ...campos, federation: e.target.value })
                }
              />
            </label>

            <label>
              Número
              <input
                maxLength={60}
                value={campos.license_number}
                onChange={(e) =>
                  setCampos({ ...campos, license_number: e.target.value })
                }
              />
            </label>

            <label>
              Temporada
              <input
                maxLength={20}
                placeholder="2026/27"
                value={campos.season}
                onChange={(e) =>
                  setCampos({ ...campos, season: e.target.value })
                }
              />
            </label>

            <label>
              Caduca el
              <input
                type="date"
                value={campos.valid_to}
                onChange={(e) =>
                  setCampos({ ...campos, valid_to: e.target.value })
                }
              />
            </label>

            <label>
              Lo que costó
              <input
                inputMode="decimal"
                placeholder="35,00"
                value={campos.precio}
                onChange={(e) =>
                  setCampos({ ...campos, precio: e.target.value })
                }
              />
            </label>
          </div>

          {campos.valid_to === '' && (
            <p className="nota">
              <strong>Sin fecha de caducidad no se puede avisar.</strong> Es el
              dato que hace útil esta pantalla.
            </p>
          )}

          <div className="acciones">
            <button
              type="submit"
              className="btn-primario"
              disabled={crear.isPending}
            >
              {crear.isPending ? 'Guardando…' : 'Guardar'}
            </button>
            <button
              type="button"
              className="btn-plano"
              onClick={() => setCreando(false)}
            >
              Dejarlo
            </button>
          </div>
        </form>
      )}

      {ordenadas.length === 0 ? (
        <Vacio
          titulo="No hay ninguna licencia registrada"
          explicacion="Apunta las de quien compite, con su fecha de caducidad. Es lo que evita que un alumno se quede fuera el día del campeonato."
        />
      ) : (
        <ul className="papeles">
          {ordenadas.map((l) => (
            <li
              key={l.id}
              className={l.status === 'cancelled' ? 'fila-fuera' : undefined}
            >
              <div className="aviso-cabeza">
                <strong>
                  {l.first_name} {l.last_name ?? ''}
                </strong>
                <span
                  className={`pastilla pastilla-left ${urgenciaDeLicencia(
                    l.status === 'active' ? l.days_to_expiry : null
                  )}`}
                >
                  {ESTADOS_DE_LICENCIA[l.status]}
                </span>
                <span className="tenue">{l.federation}</span>
              </div>

              <p className="tenue">
                {l.license_number ?? 'Sin número'}
                {l.season && ` · ${l.season}`}
                {l.valid_to && ` · hasta el ${fecha(l.valid_to)}`}
                {l.fee_cents !== null && ` · ${euros(l.fee_cents)}`}
              </p>

              {l.status === 'active' && (
                <p
                  className={
                    (l.days_to_expiry ?? 999) <= 30 ? 'texto-mal' : 'tenue'
                  }
                >
                  {cuantoQuedaDeLicencia(l.days_to_expiry)}
                </p>
              )}

              {puedeEditar && (
                <div className="acciones">
                  <label className="fecha-en-linea">
                    Nueva caducidad
                    <input
                      type="date"
                      onChange={(e) => {
                        if (e.target.value === '') return;
                        cambiar.mutate({
                          id: l.id,
                          cambios: {
                            valid_to: e.target.value,
                            status: 'active' as EstadoDeLicencia,
                          },
                        });
                      }}
                    />
                  </label>

                  {l.status !== 'cancelled' && (
                    <button
                      type="button"
                      className="btn-plano"
                      disabled={cambiar.isPending}
                      onClick={() =>
                        cambiar.mutate({
                          id: l.id,
                          cambios: { status: 'cancelled' },
                        })
                      }
                    >
                      Anular
                    </button>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
